/**
 * Copertura ideale: template presenze per RSA (fasce orarie × giorni × n. presenze)
 * con confronto immediato verso il fabbisogno normativo.
 */
import React, { useState } from "react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import RsaSelect from "../components/RsaSelect";
import MansioneSelect from "../components/MansioneSelect";
import TipologiaTurnoSelect from "../components/TipologiaTurnoSelect";
import {
  Card,
  Badge,
  Spinner,
  ErrorBox,
  WarningList,
  SimpleTable,
} from "../components/ui/basics";

const GIORNI = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function maskToLabel(mask) {
  if (mask === "1111111") return "Tutti i giorni";
  if (mask === "1111100") return "Lun-Ven";
  if (mask === "0000011") return "Sab-Dom";
  return GIORNI.filter((_, i) => mask?.[i] === "1").join(", ");
}

function NuovaRigaForm({ rsa, onCreated }) {
  const { apiRequest } = useAuth();
  const [form, setForm] = useState({
    mansionecodice: "",
    giorni: [true, true, true, true, true, false, false],
    tipologia: null,
    npresenze: 1,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tipologia) {
      setError("Seleziona una tipologia di turno");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiRequest("/planning/coperture", {
        method: "POST",
        body: JSON.stringify({
          rsa,
          mansionecodice: form.mansionecodice.trim().toUpperCase(),
          giornimask: form.giorni.map((g) => (g ? "1" : "0")).join(""),
          orainizio: form.tipologia.orainizio,
          orafine: form.tipologia.orafine,
          npresenze: Number(form.npresenze),
        }),
      });
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="flex flex-wrap gap-3 items-end">
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1">Mansione</span>
          <MansioneSelect
            value={form.mansionecodice}
            onChange={(v) => setForm({ ...form, mansionecodice: v || "" })}
            required
            className="border border-gray-300 rounded px-2 py-1.5 w-40"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1">
            Tipologia turno
          </span>
          <TipologiaTurnoSelect
            value={form.tipologia?.codice}
            onChange={(tipologia) => setForm({ ...form, tipologia })}
            required
            className="border border-gray-300 rounded px-2 py-1.5 w-56"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1">N. presenze</span>
          <input
            type="number"
            min="1"
            value={form.npresenze}
            onChange={(e) => setForm({ ...form, npresenze: e.target.value })}
            className="border border-gray-300 rounded px-2 py-1.5 w-20"
          />
        </label>
      </div>
      <div className="flex gap-1">
        {GIORNI.map((g, i) => (
          <button
            key={g}
            type="button"
            onClick={() => {
              const giorni = [...form.giorni];
              giorni[i] = !giorni[i];
              setForm({ ...form, giorni });
            }}
            className={`px-2 py-1 rounded text-xs border ${
              form.giorni[i]
                ? "bg-primary-600 text-white border-primary-600"
                : "bg-white text-gray-500 border-gray-300"
            }`}
          >
            {g}
          </button>
        ))}
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={saving}
        className="bg-primary-600 text-white text-sm px-4 py-1.5 rounded hover:bg-primary-700 disabled:opacity-50"
      >
        {saving ? "Salvataggio…" : "Aggiungi fascia"}
      </button>
    </form>
  );
}

/**
 * Motore di generazione automatica copertura (passo 2, sola anteprima):
 * distribuisce il fabbisogno risolto sulle combinazioni mansione↔turno
 * configurate in Impostazioni, proporzionalmente al peso. Non salva nulla
 * da sola: ogni riga proposta va accettata esplicitamente una per una
 * (POST /coperture), stesso principio "nessuna scrittura automatica" già
 * seguito dal backend.
 */
function GeneraCoperturaPanel({ rsa, onAccepted }) {
  const { apiRequest } = useAuth();
  const [proposta, setProposta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [accettate, setAccettate] = useState(new Set());

  const rigaKey = (p) =>
    `${p.mansionecodice}|${p.turnotipocodice}|${p.giornimask}`;

  const genera = async () => {
    setLoading(true);
    setError(null);
    setAccettate(new Set());
    try {
      setProposta(
        await apiRequest(
          `/planning/coperture/genera/${encodeURIComponent(rsa)}`,
        ),
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const accetta = async (p) => {
    const key = rigaKey(p);
    setSavingKey(key);
    setError(null);
    try {
      await apiRequest("/planning/coperture", {
        method: "POST",
        body: JSON.stringify({
          rsa,
          mansionecodice: p.mansionecodice,
          giornimask: p.giornimask,
          orainizio: p.orainizio,
          orafine: p.orafine,
          npresenze: p.npresenze,
        }),
      });
      setAccettate((prev) => new Set(prev).add(key));
      onAccepted();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Card
      title="Genera proposta automatica"
      subtitle="Distribuisce il fabbisogno richiesto sulle combinazioni mansione↔turno configurate in Impostazioni, in proporzione al peso di ciascuna."
      actions={
        <button
          onClick={genera}
          disabled={loading}
          className="bg-primary-600 text-white text-sm px-4 py-1.5 rounded hover:bg-primary-700 disabled:opacity-50"
        >
          {loading ? "Generazione…" : proposta ? "Rigenera" : "Genera proposta"}
        </button>
      }
    >
      {error && <ErrorBox message={error} />}
      {!proposta && !loading && !error && (
        <p className="text-sm text-gray-400 py-2">
          Nessuna proposta generata ancora per questa RSA.
        </p>
      )}
      {proposta && proposta.status !== "ok" && (
        <ErrorBox message={`RSA non calcolabile: ${proposta.status}`} />
      )}
      {proposta && proposta.status === "ok" && (
        <div className="space-y-4">
          <WarningList warnings={proposta.warnings} />
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
              Gap per mansione
            </p>
            <SimpleTable
              rowKey={(r) => r.mansionecodice}
              columns={[
                { key: "mansionecodice", label: "Mansione" },
                {
                  key: "oreRichieste",
                  label: "Richieste (h/sett)",
                  align: "right",
                },
                {
                  key: "oreCoperte",
                  label: "Proposte (h/sett)",
                  align: "right",
                },
                { key: "gapOreSett", label: "Scarto", align: "right" },
                {
                  key: "status",
                  label: "Stato",
                  render: (r) => <Badge status={r.status} />,
                },
                {
                  key: "turniEsclusi",
                  label: "",
                  render: (r) =>
                    r.turniEsclusi?.length ? (
                      <span className="text-xs text-yellow-700">
                        Tipologia non risolvibile: {r.turniEsclusi.join(", ")}
                      </span>
                    ) : null,
                },
              ]}
              rows={proposta.gap}
            />
          </div>

          {proposta.proposte.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                Righe proposte
              </p>
              <SimpleTable
                rowKey={(p) => rigaKey(p)}
                columns={[
                  { key: "mansionecodice", label: "Mansione" },
                  { key: "turnotipocodice", label: "Tipologia" },
                  {
                    key: "giorni",
                    label: "Giorni",
                    render: (p) => maskToLabel(p.giornimask),
                  },
                  {
                    key: "orario",
                    label: "Fascia",
                    render: (p) => `${p.orainizio} – ${p.orafine}`,
                  },
                  { key: "npresenze", label: "Presenze", align: "right" },
                  { key: "oreSettRiga", label: "Ore/sett", align: "right" },
                  {
                    key: "azioni",
                    label: "",
                    render: (p) => {
                      const key = rigaKey(p);
                      if (accettate.has(key))
                        return (
                          <span className="text-xs text-green-700">
                            ✓ Salvata
                          </span>
                        );
                      return (
                        <button
                          onClick={() => accetta(p)}
                          disabled={savingKey === key}
                          className="text-xs text-primary-600 hover:underline disabled:opacity-50"
                        >
                          {savingKey === key
                            ? "Salvataggio…"
                            : "Accetta e salva"}
                        </button>
                      );
                    },
                  },
                ]}
                rows={proposta.proposte}
              />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function CoperturaPage() {
  const { apiRequest } = useAuth();
  const [rsa, setRsa] = useState(null);
  const endpoint = rsa
    ? `/planning/coperture?rsa=${encodeURIComponent(rsa)}`
    : null;
  const { data, loading, error, reload } = useApi(endpoint);

  const elimina = async (id) => {
    await apiRequest(`/planning/coperture/${id}`, { method: "DELETE" });
    reload();
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-800">Copertura ideale</h2>
      <p className="text-sm text-gray-500">
        Template parametrabile delle presenze desiderate: es. reception 9-18
        lun-ven, 1 infermiere di notte tutti i giorni.
      </p>

      <RsaSelect value={rsa} onChange={setRsa} />

      {!rsa && (
        <p className="text-sm text-gray-400 py-8 text-center">
          Seleziona una RSA per configurare la copertura.
        </p>
      )}
      {loading && <Spinner />}
      {error && <ErrorBox message={error} onRetry={reload} />}

      {rsa && data && (
        <>
          {data.source === "default" && (
            <ErrorBox message="Le entità di pianificazione non sono ancora state create sul tenant Dataverse: il template non è salvabile. (Gate: scripts/setup-planning-schema.mjs)" />
          )}
          {data.source !== "default" && (
            <GeneraCoperturaPanel rsa={rsa} onAccepted={reload} />
          )}
          <Card title={`Fasce configurate — ${rsa}`}>
            <SimpleTable
              rowKey={(r) => r.id || `${r.mansionecodice}${r.orainizio}`}
              columns={[
                { key: "mansionecodice", label: "Mansione" },
                {
                  key: "giornimask",
                  label: "Giorni",
                  render: (r) => maskToLabel(r.giornimask),
                },
                {
                  key: "orario",
                  label: "Fascia",
                  render: (r) => `${r.orainizio} – ${r.orafine}`,
                },
                { key: "npresenze", label: "Presenze", align: "right" },
                {
                  key: "oreSettimana",
                  label: "Ore/sett",
                  align: "right",
                  render: (r) => r.oreSettimana?.toLocaleString("it-IT"),
                },
                {
                  key: "azioni",
                  label: "",
                  render: (r) =>
                    r.id ? (
                      <button
                        onClick={() => elimina(r.id)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Elimina
                      </button>
                    ) : null,
                },
              ]}
              rows={data.items}
            />
          </Card>
          {data.source !== "default" && (
            <Card title="Nuova fascia">
              <NuovaRigaForm rsa={rsa} onCreated={reload} />
            </Card>
          )}
        </>
      )}
    </div>
  );
}
